/**
 * @author        tae@zumper.com (Tae Yoon)
 * @copyright     Copyright (c) 2018, Zumper
 * @description   fetchMonetizedSimilarListings thunk
 */

import { isCommonAuthError } from '@zcode/nr-utils'
import type { Property } from '@zcode/state-type'
import type { Dispatch } from 'redux'
import { getFavorited, getMessaged } from '../../../../../inspectors/index'
import { logger } from '../../../../../logger'
import { loadMoreSimilarListings } from '../../../../../utils/similarListings'
import { selectFilters } from '../../../../filters/selectors/index'
import type { GetState } from '../../../../types'
import {
  doneFetchingMonetizedSimilarListings,
  errorFetchingMonetizedSimilarListings,
  receiveMonetizedSimilarListings,
  startFetchingMonetizedSimilarListings,
} from '../actions/index'
import {
  selectMonetizedSimilarListings,
  selectMonetizedSimilarListingsCurrentKey,
  selectMonetizedSimilarListingsFetchingKey,
} from '../selectors/index'

// we want to return the current promise when the same api call is made
// (we check this through key comparison). We store the current promise here so we can
// return it when needed.
let fetchingPromise = Promise.resolve()

export const fetchMonetizedSimilarListings =
  (entity: Property | null | undefined) => async (dispatch: Dispatch, getState: GetState) => {
    // if we have missing data or incorrect data, exit early
    if (!entity || (!entity.buildingId && !entity.listingId)) {
      return
    }

    const { buildingId, listingId } = entity
    const query = buildingId ? { buildingId } : { listingId }
    const state = getState()
    const filters = selectFilters(state)

    const key = JSON.stringify(query)
    const fetchingKey = selectMonetizedSimilarListingsFetchingKey(state)
    const currentKey = selectMonetizedSimilarListingsCurrentKey(state)

    if (key !== fetchingKey) {
      if (key === currentKey) {
        const monetizedSimilarListings = selectMonetizedSimilarListings(state)

        return monetizedSimilarListings
      }

      dispatch(startFetchingMonetizedSimilarListings({ key }))

      const favorited = getFavorited(state)
      const messaged = getMessaged(state)

      fetchingPromise = loadMoreSimilarListings({
        entity,
        favorited,
        filters,
        limit: 8,
        messaged,
      })
        .then((monetizedSimilarListings) => {
          const fetchingKey = selectMonetizedSimilarListingsFetchingKey(getState())

          if (fetchingKey === key) {
            dispatch(receiveMonetizedSimilarListings({ monetizedSimilarListings, key }))
            dispatch(doneFetchingMonetizedSimilarListings())
          }
        })
        .catch((error: unknown) => {
          const fetchingKey = selectMonetizedSimilarListingsFetchingKey(getState())

          if (fetchingKey === key) {
            dispatch(errorFetchingMonetizedSimilarListings({ error }))
            dispatch(doneFetchingMonetizedSimilarListings())
          }
          if (!isCommonAuthError(error)) {
            logger.debug(
              'fetchMonetizedSimilarListings failed:',
              typeof error === 'object' && error && 'data' in error ? error.data : error
            )
            return Promise.reject(error)
          }
        })
    }

    await fetchingPromise
  }
